import React, { useState } from "react";
import { 
  Mail, Phone, MapPin, Send, Building, Clock 
} from "lucide-react";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  return (
    <div className="bg-gray-50 min-h-screen font-sans text-gray-800 pt-0">
      
      {/* --- 1. PROFESSIONAL HEADER (Matching your Navbar) --- */}
      <div className="bg-[#1e293b] text-white py-16">
        <div className="container mx-auto px-4 md:px-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Contact Support</h1>
            <p className="text-gray-400 text-sm md:text-base max-w-2xl">
                We are here to help with your orders, account, and logistics.
            </p>
        </div>
      </div>

      {/* --- 2. MAIN CONTENT CONTAINER --- */}
      <div className="container mx-auto px-4 md:px-8 -mt-8 pb-16">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden grid lg:grid-cols-3">
            
            {/* LEFT: Contact Information (Dark Sidebar) */}
            <div className="bg-[#008b7a] text-white p-8 lg:p-10">
                <h3 className="text-xl font-bold mb-6">Get in touch</h3>
                <p className="text-teal-100 text-sm mb-10 leading-relaxed">
                    For bulk inquiries or supplier verification, please visit our nearest branch or contact headquarters.
                </p>

                <div className="space-y-8">
                    <div className="flex items-start gap-4">
                        <MapPin className="w-6 h-6 text-teal-200 mt-1 shrink-0" />
                        <div>
                            <h4 className="font-bold text-sm uppercase tracking-wide opacity-80 mb-1">Headquarters</h4>
                            <p className="text-sm leading-relaxed">
                                TradyFrog Pvt Ltd.<br/>
                                Sector 15, Noida,<br/>
                                Uttar Pradesh 201301, India
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start gap-4">
                        <Phone className="w-6 h-6 text-teal-200 mt-1 shrink-0" />
                        <div>
                            <h4 className="font-bold text-sm uppercase tracking-wide opacity-80 mb-1">Phone</h4>
                            <p className="text-sm">+91 9412220200</p>
                            <p className="text-xs text-teal-200 mt-1">(Mon-Sat, 9:00 AM - 7:00 PM)</p>
                        </div>
                    </div>

                    <div className="flex items-start gap-4">
                        <Mail className="w-6 h-6 text-teal-200 mt-1 shrink-0" />
                        <div>
                            <h4 className="font-bold text-sm uppercase tracking-wide opacity-80 mb-1">Email</h4>
                            <p className="text-sm">care@b2bhaart.com</p>
                            <p className="text-sm">info@tradyfrog.com</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT: Professional Form (Clean White) */}
            <div className="lg:col-span-2 p-8 lg:p-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h3>
                <form className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">Full Name</label>
                            <input 
                                type="text" name="name" 
                                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#008b7a] focus:border-transparent outline-none transition-all text-sm"
                                placeholder="John Doe"
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">Company Name</label>
                            <input 
                                type="text" name="company" 
                                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#008b7a] focus:border-transparent outline-none transition-all text-sm"
                                placeholder="Your Business Name"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Email Address</label>
                        <input 
                            type="email" name="email" 
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#008b7a] focus:border-transparent outline-none transition-all text-sm"
                            placeholder="you@company.com"
                            onChange={handleChange}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Type of Inquiry</label>
                        <select className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#008b7a] focus:border-transparent outline-none transition-all text-sm bg-white">
                            <option>General Inquiry</option>
                            <option>Order Status</option>
                            <option>Supplier Partnership</option>
                            <option>Technical Support</option>
                        </select>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Message</label>
                        <textarea 
                            rows={4} name="message"
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#008b7a] focus:border-transparent outline-none transition-all text-sm resize-none"
                            placeholder="How can we help you?"
                            onChange={handleChange}
                        ></textarea>
                    </div>

                    <button className="bg-[#1e293b] text-white px-8 py-3 rounded font-semibold text-sm hover:bg-[#008b7a] transition-colors flex items-center gap-2">
                        Send Message <Send size={16} />
                    </button>
                </form>
            </div>
        </div>

        {/* --- 3. QUICK SUPPORT CARDS --- */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
                { title: "Seller Support", desc: "Need help selling?", icon: Building },
                { title: "Disputes", desc: "Resolution center", icon: Clock },
                { title: "Logistics", desc: "Track shipments", icon: MapPin },
            ].map((item, idx) => (
                <div key={idx} className="bg-white p-6 border border-gray-200 rounded shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded text-[#008b7a]">
                        <item.icon size={24} />
                    </div>
                    <div>
                        <h4 className="font-bold text-gray-900">{item.title}</h4>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ContactUs;